// Cockpit: Bestaetigung confirm-pflichtiger Device-Commands (TQ #6673).
//
// Zeigt die `awaiting_confirm` Commands eines Geraets (z.B. von Aria angelegte
// restart_service / update_packages) + einen Freigabe-Button. Ohne Freigabe
// bleibt der Command in `awaiting_confirm` und der Agent fuehrt ihn NICHT aus.
//
//   Liste:    GET  /api/org/<slug>/devices/<id>/commands?status=awaiting_confirm  (ListOrgDeviceCommands)
//   Freigabe: POST /api/org/<slug>/devices/<id>/commands/<cmd_id>/confirm        (ConfirmDeviceCommand)

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'

interface PendingCommand {
  id: number
  command_type: string
  status: string
  requires_confirm: boolean
  created_by?: string
  created_at: string
}

interface CommandConfirmPanelProps {
  orgSlug: string
  deviceId: string
  isOperator: boolean
}

const COMMAND_LABELS: Record<string, string> = {
  restart_service: 'Service neustarten',
  update_packages: 'System-Updates',
  run_command: 'Diagnose-Befehl',
  check_backup: 'Backup pruefen',
  collect_inventory: 'Software-Inventar',
  perf_snapshot: 'Performance-Snapshot',
}

export function CommandConfirmPanel({ orgSlug, deviceId, isOperator }: CommandConfirmPanelProps) {
  const { api } = useAuth()
  const [pending, setPending] = useState<PendingCommand[]>([])
  const [busyId, setBusyId] = useState<number | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!api) return
    try {
      const r = await api.get<{ commands: PendingCommand[] }>(
        `/api/org/${orgSlug}/devices/${deviceId}/commands?status=awaiting_confirm`,
      )
      setPending(r.data?.commands ?? [])
      setErr(null)
    } catch (e) {
      const ex = e as { response?: { status?: number }; message?: string }
      // 404 = Endpoint (noch) nicht deployt -> still ignorieren, kein UI-Rauschen.
      if (ex.response?.status !== 404) setErr(ex.message ?? 'Laden fehlgeschlagen')
    }
  }, [api, orgSlug, deviceId])

  useEffect(() => {
    load()
    // Neue Aria-Eskalationen erscheinen ohne Reload — alle 10s nachladen.
    const t = setInterval(load, 10000)
    return () => clearInterval(t)
  }, [load])

  const confirm = useCallback(
    async (cmdId: number) => {
      if (!api) return
      setBusyId(cmdId)
      setErr(null)
      try {
        await api.post(`/api/org/${orgSlug}/devices/${deviceId}/commands/${cmdId}/confirm`, {})
        await load()
      } catch (e) {
        const ex = e as { response?: { data?: { error?: string } }; message?: string }
        setErr(ex.response?.data?.error ?? ex.message ?? 'Bestaetigung fehlgeschlagen')
      } finally {
        setBusyId(null)
      }
    },
    [api, orgSlug, deviceId, load],
  )

  if (pending.length === 0) return null

  return (
    <div
      style={{
        marginTop: '1.5rem',
        padding: '0.75rem',
        background: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 8,
      }}
    >
      <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.9rem' }}>
        Bestaetigung erforderlich ({pending.length})
      </h4>
      <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: '#92400e' }}>
        Diese Aktionen warten auf deine Freigabe, bevor der Agent sie ausfuehrt.
      </p>
      {pending.map((cmd) => (
        <div
          key={cmd.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.4rem 0',
            borderTop: '1px solid #fde68a',
          }}
        >
          <span style={{ fontSize: '0.85rem' }}>
            {COMMAND_LABELS[cmd.command_type] ?? cmd.command_type}
            {cmd.created_by ? (
              <span style={{ color: '#a16207', fontSize: '0.75rem' }}> · von {cmd.created_by.slice(0, 8)}</span>
            ) : null}
          </span>
          {isOperator ? (
            <button
              onClick={() => confirm(cmd.id)}
              disabled={busyId === cmd.id}
              style={{
                fontSize: '0.8rem',
                padding: '0.25rem 0.6rem',
                background: '#16a34a',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: busyId === cmd.id ? 'default' : 'pointer',
              }}
            >
              {busyId === cmd.id ? '…' : 'Freigeben'}
            </button>
          ) : (
            <span style={{ fontSize: '0.75rem', color: '#a16207' }}>nur Operator/Admin</span>
          )}
        </div>
      ))}
      {err ? <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#dc2626' }}>{err}</p> : null}
    </div>
  )
}
